"use client"

import { FC, useState, useEffect, useCallback } from "react"
import { Menu, X } from "lucide-react"
import { Logo } from '@/public/icons/id'
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useLanguage, type Lang } from "@/src/context/LanguageContext"
import { useUI } from "@/src/lib/i18n/ui"

const LANGS: { code: Lang; label: string }[] = [
  { code: "uk", label: "UA" },
  { code: "fr", label: "FR" },
]

const Header: FC = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { lang, setLang } = useLanguage()
  const t = useUI(lang)
  const router = useRouter()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    handleScroll()
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const navigateToSection = useCallback((sectionId: string) => {
    setIsMobileMenuOpen(false)
    router.push(`/#${sectionId}`)
  }, [router])

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev)
  }, [])

  return (
    <header className={`lg:px-0 px-2 py-4 lg:py-2 fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? "bg-black/80 backdrop-blur-md" : "bg-transparent"
    }`}>
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">

          {/* Logo */}
          <div className="flex items-center">
            <Link href="/" aria-label="На головну" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
              <Image src={Logo} alt="Logo" />
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="flex items-center gap-4">
            <nav className="pr-8 hidden lg:flex items-center space-x-8">
              <button
                onClick={() => navigateToSection("advantages-title")}
                className="text-white cursor-pointer hover:text-gray-300 transition-colors font-light block"
              >
                {t("navWhyUs")}
              </button>
              <button
                onClick={() => navigateToSection("services-title")}
                className="text-white cursor-pointer whitespace-nowrap hover:text-gray-300 transition-colors font-light"
              >
                {t("navServices")}
              </button>
              <Link
                href="/contacts"
                className="text-white whitespace-nowrap hover:text-gray-300 transition-colors font-light"
              >
                {t("navContacts")}
              </Link>
            </nav>

            {/* Language switcher */}
            <div className="hidden lg:flex items-center gap-1 mr-2">
              {LANGS.map(({ code, label }) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-all duration-200 ${
                    lang === code
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-gray-400 border-gray-600 hover:border-gray-400 hover:text-white"
                  }`}
                  aria-pressed={lang === code}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              onClick={() => navigateToSection("contact-title")}
              className="hidden lg:block z-10 relative font-light cursor-pointer px-6 py-2 border border-white text-white rounded-full hover:bg-white hover:text-black transition-all duration-300"
            >
              {t("navCta")}
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden text-white p-2 font-light"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="mt-4 lg:hidden bg-black/95 backdrop-blur-md">
            <nav className="flex flex-col space-y-4 px-8 py-8">
              <button
                onClick={() => navigateToSection("advantages-title")}
                className="text-white hover:text-gray-300 transition-colors text-left font-light text-3xl h-24"
              >
                {t("navWhyUsMobile")}
              </button>
              <button
                onClick={() => navigateToSection("services")}
                className="text-white hover:text-gray-300 transition-colors text-left font-light text-3xl h-24"
              >
                {t("navServicesMobile")}
              </button>
              <Link
                href="/contacts"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white hover:text-gray-300 transition-colors text-left font-light text-3xl h-24 flex items-center"
              >
                {t("navContacts")}
              </Link>

              {/* Language switcher mobile */}
              <div className="flex items-center gap-3 pt-2">
                {LANGS.map(({ code, label }) => (
                  <button
                    key={code}
                    onClick={() => setLang(code)}
                    className={`px-5 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                      lang === code
                        ? "bg-white text-black border-white"
                        : "bg-transparent text-gray-400 border-gray-600 hover:border-gray-400 hover:text-white"
                    }`}
                    aria-pressed={lang === code}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
